import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import WhatsAppIcon from '@material-ui/icons/WhatsApp';
import { useDispatch } from 'react-redux';
import {
    sendEvCommunication,
    getEvTelematicsLive,
} from '../../../store/actions/settingsActionCreator';

interface SendLocationModalProps {
    open: boolean;
    onClose: () => void;
    row: any;
}

const LABEL_STYLE: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 700,
    color: '#8A93B4',
    letterSpacing: 0.6,
    textTransform: 'uppercase',
};

const VALUE_STYLE: React.CSSProperties = {
    fontSize: 14,
    fontWeight: 700,
    color: '#14112D',
    marginTop: 2,
    wordBreak: 'break-word',
};

const minutesAgo = (ts: any): number | null => {
    if (!ts) return null;
    const t = typeof ts === 'number' ? ts : Date.parse(String(ts));
    if (!t || isNaN(t)) return null;
    const diffMs = Date.now() - t;
    if (diffMs < 0) return 0;
    return Math.max(0, Math.round(diffMs / 60000));
};

const Field: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
    <Box>
        <Typography component="div" style={LABEL_STYLE}>
            {label}
        </Typography>
        <Typography component="div" style={VALUE_STYLE}>
            {value ?? '-'}
        </Typography>
    </Box>
);

const SendLocationModal: React.FC<SendLocationModalProps> = ({
    open,
    onClose,
    row,
}) => {
    const dispatch = useDispatch();
    const [sending, setSending] = useState(false);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const [liveTelematics, setLiveTelematics] = useState<any | null>(null);
    const [telematicsLoading, setTelematicsLoading] = useState(false);

    const evId = (() => {
        if (!row) return null;
        if (row.evId !== undefined && row.evId !== null) return row.evId;
        if (row.id !== undefined && row.id !== null) return row.id;
        if (row.evDetails && row.evDetails.id !== undefined && row.evDetails.id !== null) return row.evDetails.id;
        return null;
    })();

    useEffect(() => {
        if (!open || !evId) {
            setLiveTelematics(null);
            return;
        }
        setTelematicsLoading(true);
        dispatch(
            getEvTelematicsLive(
                evId,
                (resp: any) => {
                    setTelematicsLoading(false);
                    // BE returns ServiceResponse<EvTelematicsLiveResponse>{ evTelematicInfo: {...} }
                    const outer = resp?.response ?? resp?.data ?? resp ?? null;
                    const info = outer?.evTelematicInfo ?? outer ?? null;
                    setLiveTelematics(info);
                },
                () => {
                    setTelematicsLoading(false);
                    setLiveTelematics(null);
                },
            ),
        );
    }, [dispatch, open, evId]);

    // Prefer freshly-fetched telematics over the cached row
    const telematicsSource = liveTelematics ?? row?.evTelematicInfo ?? {};

    const rider = row?.taggedRiderDetails?.name ?? row?.riderName ?? '-';
    const mobile =
        row?.taggedRiderDetails?.contactNumber ??
        row?.riderContactNumber ??
        '-';
    const evNo = row?.evVehicleNumber ?? '-';
    const chassis =
        row?.evNumber ??
        row?.evChassisNumber ??
        row?.chassisNumber ??
        row?.evDetails?.chassisNumber ??
        '-';
    const status =
        !!row?.blockedByCm || row?.blockStatus === 'BLOCKED'
            ? 'Blocked'
            : row?.evStatus ?? '-';
    const battery =
        telematicsSource?.vehicleBattery ??
        row?.vehicleBattery ??
        row?.evTelematicInfo?.batteryPercentage ??
        row?.batteryPercentage ??
        null;
    const batteryStr = battery !== null && battery !== undefined ? `${battery}%` : '-';

    const tl =
        row?.tlDetails ??
        row?.teamLead ??
        row?.tl ??
        row?.captainDetails ??
        {};
    const tlName = tl?.name ?? row?.tlName ?? row?.captainName ?? null;
    const tlMobile =
        tl?.contactNumber ??
        tl?.mobile ??
        row?.tlContactNumber ??
        row?.captainContactNumber ??
        null;

    // Recipient is ALWAYS the TL — the rider info is referenced inside the message body only.
    const recipientMobile = tlMobile;

    // Prefer fresh telematics from /org/ev/{evId}/telematics-live, fall back to cached row
    const energyLat =
        telematicsSource?.energyPartnerLatitude ??
        row?.evEnergyPartnerInfo?.latitude ??
        row?.energyLatitude ??
        null;
    const energyLng =
        telematicsSource?.energyPartnerLongitude ??
        row?.evEnergyPartnerInfo?.longitude ??
        row?.energyLongitude ??
        null;
    const telLat =
        telematicsSource?.latitude ?? row?.telematicLatitude ?? null;
    const telLng =
        telematicsSource?.longitude ?? row?.telematicLongitude ?? null;
    const energyUrl =
        energyLat && energyLng
            ? `https://maps.google.com/?q=${energyLat},${energyLng}`
            : null;
    const telUrl =
        telLat && telLng
            ? `https://maps.google.com/?q=${telLat},${telLng}`
            : null;

    // Last ping = most recent of telematics / energy-partner recorded_timestamp
    const telPingMs = (() => {
        const t = telematicsSource?.recordedTimestamp ?? null;
        const n = t ? Date.parse(String(t)) : NaN;
        return isNaN(n) ? null : n;
    })();
    const energyPingMs = (() => {
        const t = telematicsSource?.energyPartnerRecordedTimestamp ?? null;
        const n = t ? Date.parse(String(t)) : NaN;
        return isNaN(n) ? null : n;
    })();
    const latestPingMs =
        telPingMs && energyPingMs
            ? Math.max(telPingMs, energyPingMs)
            : telPingMs ?? energyPingMs ?? null;
    const lastPingMin = latestPingMs !== null ? minutesAgo(latestPingMs) : null;

    // Every line + structural punctuation must match the approved HSM template
    // ev_send_loc_to_tl exactly, otherwise Gupshup returns MSG_DOES_NOT_MATCH_HSM_TEMPLATE.
    const energyUrlText = energyUrl ?? 'Not available';
    const telUrlText = telUrl ?? 'Not available';
    const pingMinutesText = lastPingMin !== null ? String(lastPingMin) : '?';
    const statusText = status && status !== '-' ? status : 'Unknown';
    const batteryNumber = (() => {
        if (battery === null || battery === undefined) return '?';
        const n = String(battery).replace(/[^\d.-]/g, '');
        return n || '?';
    })();
    const tlGreeting = tlName || 'Team Lead';
    const riderName = rider && rider !== '-' ? rider : 'Unknown';
    const riderMobile = mobile && mobile !== '-' ? mobile : 'Unknown';

    const messageText = [
        `Hi ${tlGreeting}, please coordinate with rider ${riderName} (${riderMobile}).`,
        '',
        `EV ${evNo}:`,
        `Location 1 (Energy Partner): ${energyUrlText}`,
        `Location 2 (Telematics): ${telUrlText}`,
        `(Last ping ${pingMinutesText}m ago.)`,
        '',
        `Status: ${statusText} · Battery ${batteryNumber}%`,
    ].join('\n');

    const canSend = !!recipientMobile && !sending;

    const handleSend = () => {
        const phone = (recipientMobile || '').toString().replace(/\D/g, '');
        if (!phone) {
            setErrorMsg('No TL mobile available for this rider.');
            return;
        }
        setErrorMsg(null);
        setSending(true);
        dispatch(
            sendEvCommunication(
                {
                    mobile: phone,
                    template: 'EV_COORDINATE',
                    captainName: tlGreeting,
                    riderName,
                    riderPhone: riderMobile,
                    evNumber: evNo,
                    location1: energyUrlText,
                    location2: telUrlText,
                    lastPingMins: lastPingMin !== null ? lastPingMin : 0,
                    status: statusText,
                    batteryPct: batteryNumber !== '?' ? Number(batteryNumber) : 0,
                },
                () => {
                    setSending(false);
                    onClose();
                },
                (err: any) => {
                    setSending(false);
                    setErrorMsg(
                        err?.message ||
                            err?.error?.message ||
                            'Failed to send WhatsApp message',
                    );
                },
            ),
        );
    };

    if (!row) return null;

    return (
        <Dialog
            open={open}
            onClose={sending ? undefined : onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ style: { borderRadius: 14, overflow: 'hidden' } }}
        >
            <DialogTitle style={{ padding: '16px 24px' }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography
                        component="span"
                        style={{ fontSize: 16, fontWeight: 700, color: '#14112D' }}
                    >
                        Send Location to TL
                    </Typography>
                    <IconButton
                        onClick={onClose}
                        size="small"
                        style={{ padding: 4 }}
                        disabled={sending}
                    >
                        <CloseIcon style={{ fontSize: 20, color: '#7B8094' }} />
                    </IconButton>
                </Box>
            </DialogTitle>
            <Divider />
            <DialogContent style={{ padding: '20px 24px 22px' }}>
                {/* RIDER DETAILS CARD */}
                <Box
                    style={{
                        background: '#F6F7FB',
                        border: '1px solid #ECEEF6',
                        borderRadius: 12,
                        padding: '16px 18px',
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr 1fr',
                        rowGap: 14,
                        columnGap: 18,
                        marginBottom: 20,
                    }}
                >
                    <Field label="Rider" value={rider} />
                    <Field label="Mobile" value={mobile} />
                    <Field label="EV No" value={evNo} />
                    <Field label="Chassis" value={chassis} />
                    <Field label="Status" value={status} />
                    <Field label="Battery" value={batteryStr} />
                </Box>

                {/* SENDING TO */}
                <Typography component="div" style={{ ...LABEL_STYLE, marginBottom: 6 }}>
                    Sending To
                </Typography>
                <Box
                    style={{
                        background: '#F6F7FB',
                        border: '1px solid #ECEEF6',
                        borderRadius: 10,
                        padding: '10px 14px',
                        marginBottom: 18,
                    }}
                >
                    {tlMobile ? (
                        <Typography style={{ fontSize: 14, color: '#14112D', fontWeight: 700 }}>
                            {tlName || 'Team Lead'}
                            <span style={{ color: '#3B3F52', fontWeight: 600 }}>
                                {' · '}
                                {tlMobile}
                            </span>
                        </Typography>
                    ) : (
                        <Typography
                            style={{ fontSize: 13, color: '#B45309', fontStyle: 'italic' }}
                        >
                            No TL assigned for this rider — cannot send
                        </Typography>
                    )}
                </Box>

                {/* MESSAGE PREVIEW */}
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    style={{ marginBottom: 6 }}
                >
                    <Typography component="div" style={LABEL_STYLE}>
                        Message Preview
                    </Typography>
                    {telematicsLoading && (
                        <Typography
                            component="span"
                            style={{ fontSize: 11, color: '#7B8094', fontStyle: 'italic' }}
                        >
                            Fetching live location…
                        </Typography>
                    )}
                </Box>
                <Box
                    style={{
                        background: '#ECFDF5',
                        border: '1px solid #A7F3D0',
                        borderRadius: 10,
                        padding: '14px 16px',
                        fontSize: 13,
                        color: '#064E3B',
                        lineHeight: 1.7,
                    }}
                >
                    <Box>
                        Hi <strong>{tlGreeting}</strong>, please coordinate with rider{' '}
                        <strong>{riderName}</strong> ({riderMobile}).
                    </Box>

                    <Box style={{ marginTop: 10 }}>
                        EV <strong>{evNo}</strong>:
                    </Box>
                    <Box>
                        Location 1 (Energy Partner):{' '}
                        {energyUrl ? (
                            <a
                                href={energyUrl}
                                target="_blank"
                                rel="noreferrer"
                                style={{ color: '#1D4ED8', textDecoration: 'underline' }}
                            >
                                {energyUrl}
                            </a>
                        ) : (
                            <span style={{ color: '#4B6358' }}>Not available</span>
                        )}
                    </Box>
                    <Box>
                        Location 2 (Telematics):{' '}
                        {telUrl ? (
                            <a
                                href={telUrl}
                                target="_blank"
                                rel="noreferrer"
                                style={{ color: '#1D4ED8', textDecoration: 'underline' }}
                            >
                                {telUrl}
                            </a>
                        ) : (
                            <span style={{ color: '#4B6358' }}>Not available</span>
                        )}
                    </Box>
                    <Box style={{ color: '#4B6358' }}>
                        (Last ping {pingMinutesText}m ago.)
                    </Box>

                    <Box style={{ marginTop: 10 }}>
                        Status: <strong>{statusText}</strong> · Battery{' '}
                        <strong>{batteryNumber}%</strong>
                    </Box>
                </Box>

                {errorMsg && (
                    <Box
                        style={{
                            marginTop: 12,
                            padding: '10px 12px',
                            background: '#FEF2F2',
                            border: '1px solid #FECACA',
                            borderRadius: 8,
                            fontSize: 12.5,
                            color: '#B91C1C',
                        }}
                    >
                        {errorMsg}
                    </Box>
                )}

                {/* ACTIONS */}
                <Box
                    display="flex"
                    justifyContent="flex-end"
                    alignItems="center"
                    style={{ gap: 10, marginTop: 20 }}
                >
                    <Button
                        onClick={onClose}
                        variant="outlined"
                        disabled={sending}
                        style={{
                            borderRadius: 8,
                            textTransform: 'none',
                            fontWeight: 600,
                            padding: '7px 18px',
                            borderColor: '#D7DBE8',
                            color: '#3B3F52',
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSend}
                        disabled={!canSend}
                        variant="contained"
                        startIcon={
                            !sending ? <WhatsAppIcon style={{ fontSize: 16 }} /> : undefined
                        }
                        style={{
                            borderRadius: 8,
                            textTransform: 'none',
                            fontWeight: 700,
                            padding: '7px 18px',
                            background: canSend ? '#25D366' : '#9CA3AF',
                            color: '#fff',
                            boxShadow: 'none',
                            opacity: sending ? 0.7 : 1,
                        }}
                    >
                        {sending ? 'Sending…' : 'Send via WhatsApp'}
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default SendLocationModal;
